import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  Server,
  Inbox,
  CheckCircle2,
  Clock,
  RefreshCw,
  Loader2,
  Zap,
  AlertTriangle,
  Wifi,
  WifiOff,
} from 'lucide-react'
import { jobsApi, type WorkerInfo, type ActiveTask } from '../../api/endpoints'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'

// Auto-refresh interval
const POLL_INTERVAL = 5000

export default function TaskMonitorPage() {
  const queryClient = useQueryClient()
  const [revoking, setRevoking] = useState<string | null>(null)

  const { data: stats, isLoading: statsLoading, isError: statsError } = useQuery({
    queryKey: ['worker-stats'],
    queryFn: () => jobsApi.workerStats(),
    refetchInterval: POLL_INTERVAL,
    refetchIntervalInBackground: false,  // stop polling when tab is hidden
    staleTime: 4000,                     // don't refetch if data is < 4s old
    gcTime: 0,                           // don't cache when navigating away
  })

  const { data: queues } = useQuery({
    queryKey: ['queue-lengths'],
    queryFn: () => jobsApi.queueLengths(),
    refetchInterval: POLL_INTERVAL,
    refetchIntervalInBackground: false,
    staleTime: 4000,
    gcTime: 0,
  })

  const revokeMutation = useMutation({
    mutationFn: (taskId: string) => jobsApi.revokeJob(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worker-stats'] })
      setRevoking(null)
    },
  })

  const handleRevoke = (taskId: string) => {
    setRevoking(taskId)
    revokeMutation.mutate(taskId)
  }

  const summary = stats?.summary || { workers_online: 0, active: 0, reserved: 0, completed: 0 }
  const queueMessages = queues?.queues?.[0]?.messages ?? 0
  const queueConsumers = queues?.queues?.[0]?.consumers ?? 0

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">Celery workers, RabbitMQ queues, and active jobs</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['worker-stats'] })
            queryClient.invalidateQueries({ queryKey: ['queue-lengths'] })
          }}
        >
          <RefreshCw size={16} /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          icon={Server}
          label="Workers"
          value={summary.workers_online}
          color={summary.workers_online > 0 ? 'green' : 'red'}
        />
        <StatCard
          icon={Zap}
          label="Running"
          value={summary.active}
          color={summary.active > 0 ? 'indigo' : 'gray'}
        />
        <StatCard
          icon={Inbox}
          label="Queued"
          value={summary.reserved + queueMessages}
          color={summary.reserved + queueMessages > 0 ? 'amber' : 'gray'}
        />
        <StatCard
          icon={CheckCircle2}
          label="Completed"
          value={summary.completed}
          color="gray"
        />
        <StatCard
          icon={Activity}
          label="Consumers"
          value={queueConsumers}
          color="gray"
        />
      </div>

      {statsLoading ? (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <Loader2 size={24} className="animate-spin mr-2" /> Loading worker stats...
        </div>
      ) : statsError ? (
        <OfflineBanner />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Workers Panel */}
          <div className="xl:col-span-1">
            <WorkersPanel workers={stats?.workers || []} />
          </div>

          {/* Active & Queued Tasks */}
          <div className="xl:col-span-2 space-y-6">
            <TasksPanel
              title="Running Tasks"
              icon={Zap}
              tasks={stats?.active_tasks || []}
              showDuration
              onRevoke={handleRevoke}
              revoking={revoking}
            />
            <TasksPanel
              title="Queued Tasks"
              icon={Clock}
              tasks={stats?.reserved_tasks || []}
            />
          </div>
        </div>
      )}
    </div>
  )
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType
  label: string
  value: number
  color: string
}) {
  const colorMap: Record<string, string> = {
    green: 'text-green-600 bg-green-50',
    red: 'text-red-600 bg-red-50',
    indigo: 'text-indigo-600 bg-indigo-50',
    amber: 'text-amber-600 bg-amber-50',
    gray: 'text-gray-600 bg-gray-50',
  }
  const styles = colorMap[color] || colorMap.gray

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${styles}`}>
          <Icon size={18} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  )
}


function WorkersPanel({ workers }: { workers: WorkerInfo[] }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <Server size={16} className="text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-900">Workers</h2>
        <Badge variant={workers.length > 0 ? 'success' : 'danger'}>
          {workers.length} online
        </Badge>
      </div>

      {workers.length === 0 ? (
        <div className="p-6 text-center">
          <WifiOff size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No workers online</p>
          <p className="text-xs text-gray-400 mt-1">Start a worker with: celery -A src.worker worker</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {workers.map((worker) => (
            <div key={worker.name} className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wifi size={14} className="text-green-500" />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {worker.name.split('@')[1] || worker.name}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                <div>PID: <span className="text-gray-700">{worker.pid || '—'}</span></div>
                <div>Concurrency: <span className="text-gray-700">{worker.concurrency}</span></div>
                <div>Active: <span className="text-gray-700">{worker.active_tasks}</span></div>
                <div>Completed: <span className="text-gray-700">{worker.completed}</span></div>
              </div>

              {/* Task breakdown */}
              {Object.keys(worker.total_by_task).length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-50">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Completed by type</p>
                  <div className="space-y-1">
                    {Object.entries(worker.total_by_task).map(([task, count]) => (
                      <div key={task} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{task}</span>
                        <span className="text-gray-900 font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


function TasksPanel({
  title,
  icon: Icon,
  tasks,
  showDuration,
  onRevoke,
  revoking,
}: {
  title: string
  icon: React.ElementType
  tasks: ActiveTask[]
  showDuration?: boolean
  onRevoke?: (id: string) => void
  revoking?: string | null
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <Icon size={16} className="text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {tasks.length > 0 && (
          <Badge variant={showDuration ? 'info' : 'default'}>{tasks.length}</Badge>
        )}
      </div>

      {tasks.length === 0 ? (
        <div className="p-6 text-center">
          <CheckCircle2 size={28} className="mx-auto text-gray-200 mb-2" />
          <p className="text-sm text-gray-400">No {title.toLowerCase()}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                <th className="px-4 py-2 text-left font-medium">Task</th>
                <th className="px-4 py-2 text-left font-medium">Worker</th>
                {showDuration && <th className="px-4 py-2 text-left font-medium">Duration</th>}
                <th className="px-4 py-2 text-left font-medium">ID</th>
                {onRevoke && <th className="px-4 py-2 text-right font-medium">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {showDuration && (
                        <Loader2 size={14} className="text-indigo-500 animate-spin" />
                      )}
                      <span className="text-sm font-medium text-gray-900">{task.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {task.worker.split('@')[1] || task.worker}
                  </td>
                  {showDuration && (
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <RunningDuration startTime={task.started} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <code className="text-xs text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                      {task.id.slice(0, 8)}
                    </code>
                  </td>
                  {onRevoke && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => onRevoke(task.id)}
                        disabled={revoking === task.id}
                        className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                      >
                        {revoking === task.id ? 'Revoking...' : 'Revoke'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}


function RunningDuration({ startTime }: { startTime: number | null }) {
  const [, setTick] = useState(0)

  // Force re-render every second for live duration
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(interval)
  }, [])

  if (!startTime) return <span className="text-gray-400">—</span>

  const elapsed = Math.floor(Date.now() / 1000 - startTime)
  if (elapsed < 0) return <span className="text-gray-400">—</span>

  const mins = Math.floor(elapsed / 60)
  const secs = elapsed % 60

  const color = elapsed > 120 ? 'text-red-600' : elapsed > 60 ? 'text-amber-600' : 'text-gray-600'

  return (
    <span className={`font-mono text-xs ${color}`}>
      {mins > 0 ? `${mins}m ` : ''}{secs}s
    </span>
  )
}


function OfflineBanner() {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-4">
      <AlertTriangle size={24} className="text-amber-500 mt-0.5" />
      <div>
        <h3 className="text-sm font-semibold text-amber-800">Workers Unavailable</h3>
        <p className="text-sm text-amber-700 mt-1">
          Cannot connect to the task queue. Make sure RabbitMQ and Redis are running.
        </p>
        <div className="mt-3 space-y-1 text-xs text-amber-600 font-mono">
          <p>docker compose up redis rabbitmq</p>
          <p>celery -A src.worker worker --loglevel=info</p>
        </div>
      </div>
    </div>
  )
}
